import React, { useState, useEffect, useCallback } from "react";
import api from "../../../../api";
import { useParams } from "react-router-dom";
import { Modal, Badge } from "react-bootstrap";
import Swal from "sweetalert2";
import VerifiedCopyActions from "../../Common/VerifiedCopyActions";

/* ============================================================
 SUB CARD COMPONENT
 ============================================================ */
const SubCard = ({ indent, onPreview, onDownload, onDownloadExcel, onRefresh }) => (
 <div className="card shadow-sm rounded mb-4" style={{ minHeight: "260px" }}>
 <div className="card-body">
 {/* HEADER */}
 <div className="d-flex justify-content-between mb-2">
 <span className="fw-bold text-dark">
 {indent.indentNumber || indent._id}
 </span>
 </div>

 {indent.project && (
 <p className="text-muted small mb-1">
 <strong>Project:</strong> {indent.project.project}
 </p>
 )}

 {/* ===== ITEMS LIST ===== */}
 <div className="mb-2">
 <strong>Items:</strong>
 <ul className="mb-0 small">
 {indent.items.map((item, i) => (
 <li key={i}>
 {item.itemName} — {item.quantityRequested} {item.unit}
 </li>
 ))}
 </ul>
 </div>

 {/* STATUS */}
 <Badge
 className="rounded-pill mt-2"
 bg={
 indent.status === "Approved"
 ? "success"
 : indent.status === "Rejected"
 ? "danger"
 : indent.status === "Pending"
 ? "warning"
 : "secondary"
 }
 >
 {indent.status}
 </Badge>

 {/* DATES */}
 <div className="mt-3 text-muted small">
 {indent.requiredByDate && (
 <p>
 Required: {new Date(indent.requiredByDate).toLocaleDateString()}
 </p>
 )}
 <p>Created: {new Date(indent.createdAt).toLocaleDateString()}</p>
 </div>

 {/* USERS */}
 {indent.requestedBy && (
 <p className="small mt-2 mb-1">
 <strong>Requested By:</strong> {indent.requestedBy.name}
 </p>
 )}

 {indent.approvedBy && (
 <p className="small mb-1">
 <strong>Approved By:</strong> {indent.approvedBy.name}
 </p>
 )}

 {/* ACTION BUTTONS */}
 <div className="mt-2 d-flex gap-1 flex-wrap align-items-center">
 <button
 className="btn btn-sm btn-outline-primary"
 onClick={() => onPreview(indent._id)}
 title="Preview PDF"
 >
 <i className="fas fa-eye"></i>
 </button>

 <button
 className="btn btn-sm btn-outline-danger"
 onClick={() => onDownload(indent._id)}
 title="Download PDF"
 >
 <i className="fas fa-file-pdf"></i>
 </button>

 <button
 className="btn btn-sm btn-outline-success"
 onClick={() => onDownloadExcel(indent)}
 title="Download Excel"
 >
 <i className="fas fa-file-excel"></i>
 </button>

 <VerifiedCopyActions
 id={indent._id}
 type="indents"
 hasVerifiedCopy={!!(indent.verifiedCopy?.filePath || indent.verifiedCopy?.fileUrl || indent.signedCopy?.filePath || indent.signedCopy?.fileUrl)}
 onUploadSuccess={onRefresh}
 />
 </div>
 </div>
 </div>
);

/* ============================================================
 MATERIAL TAB
============================================================ */

const MaterialTab = () => {
 const { id: workOrderId } = useParams();

 const [groups, setGroups] = useState([]);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState("");
 const [previewingPdf, setPreviewingPdf] = useState(false);

 // Modal states
 const [showPdf, setShowPdf] = useState(false);
 const [pdfUrl, setPdfUrl] = useState("");

 const fetchIndents = useCallback(async () => {
 try {
 const res = await api.get(`/indents/work-order/${workOrderId}`);

 if (!res.data.success) {
 setError("Failed to load indents");
 return;
 }

 const indents = res.data.data;
 const statuses = ["Pending", "Approved", "Rejected"];
 const grouped = statuses.map((status) => {
 const filtered = indents.filter((i) => i.status === status);

 return {
 status,
 cards: filtered, // [OK] one card per indent
 };
 });

 setGroups(grouped);
 } catch (err) {
 setError("Error fetching indents");
 } finally {
 setLoading(false);
 }
 }, [workOrderId]);

 useEffect(() => {
 if (workOrderId) fetchIndents();
 }, [workOrderId, fetchIndents]);

 if (loading) return <p>Loading material indents...</p>;
 if (error) return <p className="text-danger">{error}</p>;

 /* ============================================================
 HANDLERS for PDF
 ============================================================= */
 const handlePreviewPDF = async (id) => {
 try {
 setPreviewingPdf(true);

 const res = await api.get(`/indents/${id}/pdf`, {
 responseType: "blob",
 });

 const blob = new Blob([res.data], { type: "application/pdf" });
 const fileURL = window.URL.createObjectURL(blob);

 setPdfUrl(fileURL);
 setShowPdf(true);
 } catch (err) {
 console.error("Indent PDF preview error:", err);

 Swal.fire({
 icon: "error",
 title: "Preview Failed",
 text: "Unable to preview the indent PDF.",
 });
 } finally {
 setPreviewingPdf(false);
 }
 };

 const handleDownloadPDF = async (id) => {
 try {
 const res = await api.get(`/indents/${id}/pdf`, {
 responseType: "blob",
 });

 const url = window.URL.createObjectURL(new Blob([res.data]));
 const a = document.createElement("a");
 a.href = url;
 a.download = `INDENT-${id}.pdf`;
 a.click();

 Swal.fire({
 icon: "success",
 title: "Downloaded",
 text: "Indent PDF downloaded successfully",
 timer: 1500,
 showConfirmButton: false,
 });
 } catch (err) {
 console.error("Indent PDF download error:", err);

 Swal.fire({
 icon: "error",
 title: "Download Failed",
 text: "Unable to download the indent PDF.",
 });
 }
 };

 const handleDownloadExcel = async (indent) => {
 try {
 const id = indent._id || indent;
 const res = await api.get(`/indents/${id}/excel`, {
 responseType: "blob",
 });

 const cleanIndentNo = (indent.indentNumber || `INDENT-${id}`).replace(/[^a-z0-9]/gi, "_");
 const url = window.URL.createObjectURL(new Blob([res.data], {
 type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
 }));
 const a = document.createElement("a");
 a.href = url;
 a.download = `${cleanIndentNo}_Items.xlsx`;
 a.click();
 window.URL.revokeObjectURL(url);

 Swal.fire({
 icon: "success",
 title: "Downloaded",
 text: "Indent Excel downloaded successfully",
 timer: 1500,
 showConfirmButton: false,
 });
 } catch (err) {
 console.error("Indent Excel download error:", err);
 Swal.fire({
 icon: "error",
 title: "Download Failed",
 text: "Unable to download the indent Excel.",
 });
 }
 };

 return (
 <div className="container-fluid">
 {/* ================== PDF MODAL ================== */}
 <Modal show={showPdf} onHide={() => setShowPdf(false)} size="lg" centered>
 <Modal.Header closeButton>
 <Modal.Title>Indent PDF Preview</Modal.Title>
 </Modal.Header>
 <Modal.Body style={{ height: "80vh" }}>
 {pdfUrl ? (
 <iframe
 src={pdfUrl}
 title="PDF Preview"
 width="100%"
 height="100%"
 style={{ border: "none" }}
 />
 ) : (
 <p className="text-center">No PDF available</p>
 )}
 </Modal.Body>
 </Modal>

 {groups.map((group, idx) => (
 <div key={idx} className="mb-5">
 <h5 className="fw-bold mb-3">
 {group.status} ({group.cards.length})
 </h5>

 <div className="row">
 {group.cards.map((indent, index) => (
 <div key={indent._id} className="col-lg-3 col-md-4 col-sm-6">
 <SubCard
 indent={indent}
 onPreview={handlePreviewPDF}
 onDownload={handleDownloadPDF}
 onDownloadExcel={handleDownloadExcel}
 onRefresh={fetchIndents}
 />
 </div>
 ))}
 </div>
 </div>
 ))}

 {/* Loader Overlay */}
 {previewingPdf && (
 <div
 className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
 style={{ background: "rgba(0,0,0,0.4)", zIndex: 9999 }}
 >
 <div className="bg-white p-4 rounded shadow text-center">
 <div className="spinner-border text-primary mb-3"></div>
 <h6 className="mb-0">Preparing Indent PDF…</h6>
 </div>
 </div>
 )}
 </div>
 );
};

export default MaterialTab;
